import React, { useState } from "react";
import { getApiErrorPayload } from "../../utils/apiError";

export interface ClientFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  ssnLast4: string;
  dateOfBirth: string; // ISO date string
  equifaxScore: number;
  experianScore: number;
  transUnionScore: number;
  croaAccepted: boolean;
}

export interface ClientFormProps {
  mode: "create" | "edit";
  initialValues?: Partial<ClientFormValues>;
  submitLabel: string;
  onSubmit: (values: ClientFormValues) => Promise<void>;
  onCancel: () => void;
  serverError?: string;
}

type FieldKey =
  | "firstName"
  | "lastName"
  | "email"
  | "phone"
  | "address"
  | "city"
  | "state"
  | "zipCode"
  | "ssnLast4"
  | "dateOfBirth"
  | "equifaxScore"
  | "experianScore"
  | "transUnionScore"
  | "croa";

// Convert an ISO timestamp (or already-YYYY-MM-DD string) into the YYYY-MM-DD
// shape that <input type="date"> expects. Used when prefilling edit mode.
const isoToDateInput = (iso: string | undefined): string => {
  if (!iso) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const ClientForm: React.FC<ClientFormProps> = ({
  mode,
  initialValues,
  submitLabel,
  onSubmit,
  onCancel,
  serverError,
}) => {
  const [firstName, setFirstName] = useState(initialValues?.firstName ?? "");
  const [lastName, setLastName] = useState(initialValues?.lastName ?? "");
  const [email, setEmail] = useState(initialValues?.email ?? "");
  const [phone, setPhone] = useState(initialValues?.phone ?? "");
  const [address, setAddress] = useState(initialValues?.address ?? "");
  const [city, setCity] = useState(initialValues?.city ?? "");
  const [state, setState] = useState(initialValues?.state ?? "");
  const [zipCode, setZipCode] = useState(initialValues?.zipCode ?? "");
  const [dateOfBirth, setDateOfBirth] = useState(
    isoToDateInput(initialValues?.dateOfBirth)
  );
  const [ssnLast4, setSsnLast4] = useState(initialValues?.ssnLast4 ?? "");
  const [equifaxScore, setEquifaxScore] = useState<string>(
    initialValues?.equifaxScore != null ? String(initialValues.equifaxScore) : ""
  );
  const [experianScore, setExperianScore] = useState<string>(
    initialValues?.experianScore != null ? String(initialValues.experianScore) : ""
  );
  const [transUnionScore, setTransUnionScore] = useState<string>(
    initialValues?.transUnionScore != null ? String(initialValues.transUnionScore) : ""
  );
  const [croaAccepted, setCroaAccepted] = useState(
    initialValues?.croaAccepted ?? false
  );

  const [submitting, setSubmitting] = useState(false);
  const [internalError, setInternalError] = useState("");

  // In edit mode, suppress the CROA checkbox once the row is already signed —
  // re-acknowledgement isn't meaningful. Show it for legacy rows that predate
  // CROA tracking. Create mode always shows + requires it.
  const showCroaCheckbox = mode === "create" || !initialValues?.croaAccepted;
  const requireCroa = mode === "create";

  const validate = (): Record<FieldKey, boolean> => {
    return {
      firstName: !firstName.trim(),
      lastName: !lastName.trim(),
      email: !email.trim(),
      phone: !phone.trim(),
      address: !address.trim(),
      city: !city.trim(),
      state: !state.trim(),
      zipCode: !zipCode.trim(),
      ssnLast4: !ssnLast4.trim(),
      dateOfBirth: !dateOfBirth.trim(),
      equifaxScore: equifaxScore.trim() === "",
      experianScore: experianScore.trim() === "",
      transUnionScore: transUnionScore.trim() === "",
      croa: requireCroa && !croaAccepted,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInternalError("");

    const inv = validate();
    if (Object.values(inv).some(Boolean)) {
      setInternalError(
        "Please fill in all required fields, including the CROA disclosure acknowledgement."
      );
      return;
    }

    const eq = parseInt(equifaxScore, 10);
    const ex = parseInt(experianScore, 10);
    const tu = parseInt(transUnionScore, 10);
    if (Number.isNaN(eq) || Number.isNaN(ex) || Number.isNaN(tu)) {
      setInternalError("Credit scores must be valid numbers.");
      return;
    }

    let dobIso: string;
    const dobDate = new Date(dateOfBirth);
    if (Number.isNaN(dobDate.getTime())) {
      setInternalError("Date of birth is not a valid date.");
      return;
    }
    dobIso = dobDate.toISOString();

    const values: ClientFormValues = {
      firstName,
      lastName,
      email,
      phone,
      address,
      city,
      state,
      zipCode,
      ssnLast4,
      dateOfBirth: dobIso,
      equifaxScore: eq,
      experianScore: ex,
      transUnionScore: tu,
      // For edit mode with an already-signed row the checkbox is hidden; preserve
      // the prior accepted=true so the parent can still build a complete payload.
      croaAccepted: showCroaCheckbox ? croaAccepted : true,
    };

    setSubmitting(true);
    try {
      await onSubmit(values);
    } catch (err: unknown) {
      const payload = getApiErrorPayload(err);
      const fallback =
        err instanceof Error
          ? err.message
          : "Could not save client. Check your connection and try again.";
      setInternalError(payload?.message ?? fallback);
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none ring-blue-500 placeholder:text-slate-600 focus:ring-2";
  const labelBase = "block text-xs font-medium uppercase tracking-wide text-slate-400";

  const displayError = serverError || internalError;

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="space-y-6 rounded-xl border border-slate-800 bg-slate-900 p-6 shadow-lg shadow-black/20"
    >
      {displayError && (
        <div className="rounded-lg border border-red-500/40 bg-red-950/40 px-3 py-2 text-sm text-red-200">
          {displayError}
        </div>
      )}

      <fieldset className="space-y-4">
        <legend className="text-sm font-semibold text-slate-200">Contact</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelBase} htmlFor="firstName">
              First name <span className="text-red-400">*</span>
            </label>
            <input
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={inputBase}
              required
            />
          </div>
          <div>
            <label className={labelBase} htmlFor="lastName">
              Last name <span className="text-red-400">*</span>
            </label>
            <input
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={inputBase}
              required
            />
          </div>
          <div>
            <label className={labelBase} htmlFor="email">
              Email <span className="text-red-400">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputBase}
              required
            />
          </div>
          <div>
            <label className={labelBase} htmlFor="phone">
              Phone <span className="text-red-400">*</span>
            </label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={inputBase}
              required
            />
          </div>
        </div>
      </fieldset>

      <fieldset className="space-y-4">
        <legend className="text-sm font-semibold text-slate-200">Address</legend>
        <div>
          <label className={labelBase} htmlFor="address">
            Street address <span className="text-red-400">*</span>
          </label>
          <input
            id="address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className={inputBase}
            required
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="sm:col-span-1">
            <label className={labelBase} htmlFor="city">
              City <span className="text-red-400">*</span>
            </label>
            <input
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className={inputBase}
              required
            />
          </div>
          <div>
            <label className={labelBase} htmlFor="state">
              State <span className="text-red-400">*</span>
            </label>
            <input
              id="state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              className={inputBase}
              maxLength={2}
              required
            />
          </div>
          <div>
            <label className={labelBase} htmlFor="zipCode">
              ZIP <span className="text-red-400">*</span>
            </label>
            <input
              id="zipCode"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              className={inputBase}
              required
            />
          </div>
        </div>
      </fieldset>

      <fieldset className="space-y-4">
        <legend className="text-sm font-semibold text-slate-200">Identity</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={labelBase} htmlFor="dateOfBirth">
              Date of birth <span className="text-red-400">*</span>
            </label>
            <input
              id="dateOfBirth"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className={inputBase}
              required
            />
          </div>
          <div>
            <label className={labelBase} htmlFor="ssnLast4">
              SSN last 4 <span className="text-red-400">*</span>
            </label>
            <input
              id="ssnLast4"
              inputMode="numeric"
              maxLength={4}
              value={ssnLast4}
              onChange={(e) =>
                setSsnLast4(e.target.value.replace(/\D/g, "").slice(0, 4))
              }
              className={inputBase}
              required
            />
          </div>
        </div>
      </fieldset>

      <fieldset className="space-y-4">
        <legend className="text-sm font-semibold text-slate-200">
          Credit bureau scores
        </legend>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className={labelBase} htmlFor="equifaxScore">
              Equifax <span className="text-red-400">*</span>
            </label>
            <input
              id="equifaxScore"
              type="number"
              min={300}
              max={850}
              value={equifaxScore}
              onChange={(e) => setEquifaxScore(e.target.value)}
              className={inputBase}
              required
            />
          </div>
          <div>
            <label className={labelBase} htmlFor="experianScore">
              Experian <span className="text-red-400">*</span>
            </label>
            <input
              id="experianScore"
              type="number"
              min={300}
              max={850}
              value={experianScore}
              onChange={(e) => setExperianScore(e.target.value)}
              className={inputBase}
              required
            />
          </div>
          <div>
            <label className={labelBase} htmlFor="transUnionScore">
              TransUnion <span className="text-red-400">*</span>
            </label>
            <input
              id="transUnionScore"
              type="number"
              min={300}
              max={850}
              value={transUnionScore}
              onChange={(e) => setTransUnionScore(e.target.value)}
              className={inputBase}
              required
            />
          </div>
        </div>
      </fieldset>

      {showCroaCheckbox && (
        <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
          <label className="flex cursor-pointer gap-3 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={croaAccepted}
              onChange={(e) => setCroaAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500"
            />
            <span>
              I acknowledge the Credit Repair Organizations Act (CROA) disclosure: you may cancel within three business
              days; we will not ask you to waive this right; no one can remove accurate negative information from your
              report; and you have the right to dispute inaccurate information directly with the bureaus.
              {requireCroa && <> <span className="text-red-400">*</span></>}
            </span>
          </label>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
        >
          {submitting ? "Saving…" : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-slate-700 px-5 py-2.5 text-sm font-medium text-slate-300 hover:bg-slate-800"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ClientForm;
